import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { catchError } from 'rxjs/operators';
import { Observable, throwError as _throw } from 'rxjs';

import { NotificationComponent } from '../../../shared/components';

import { UserRole } from '../../enum';
import { AppOptionsService } from '../app-options';
import { NotificationService } from '../notification';
import { CheckDomainResponse, Partner } from '../../interfaces';
import { AlertsInterface, PartnerInterfaceGlobal } from '../../models';
import { ErrorsHandlingService, AuthActions } from '../errors-handling';

export const REMEMBER_STORAGE_NAME = 'app-remember-token';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiURl;

  constructor(
    private http: HttpClient,
    private options: AppOptionsService,
    private notificationService: NotificationService,
    private notificationComponent: NotificationComponent,
  ) {
    this.apiURl = this.options.getApiUrl();
    this.setupAuthActions();
  }

  private setupAuthActions(): void {
    const authActions: AuthActions = {
      logout: () => this.logout(),
      isExpired: (dateExpired: string) => this.isExpired(dateExpired),
      expiredDate: this.expiredDate
    };
    ErrorsHandlingService.setAuthActions(authActions);
  }

  public isAuthenticated(): boolean {
    let token = sessionStorage.getItem('token');
    let expiredDate = sessionStorage.getItem('expiredDate');

    if (!expiredDate) {
      expiredDate = localStorage.getItem('expiredDate');
    }

    if (!token) {
      token = localStorage.getItem('token');
    }

    if (!token) {
      return false;
    }

    const isTokenExpired = this.isExpired(expiredDate || '');

    if (!isTokenExpired) {
      sessionStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
      this.showNotification(
        'error',
        'error',
        'error',
        'Your authentication token has been expired. Please log in again to continue using the platform',
        '385px',
      );
    }
    // Check whether the token is expired and return
    // true or false
    return !isTokenExpired;
  }

  public checkLimitQPSForPartner(): Observable<any> {
    return this.http.get(`${this.apiURl}/checklimitqps`).pipe();
  }

  public getSpendAlerts(): Observable<AlertsInterface.ISpendAlertResponse> {
    return this.http.get<AlertsInterface.ISpendAlertResponse>(`${this.apiURl}/alerts/spend-limit`)
      .pipe(catchError(this.handleHttpError.bind(this)));
  }

  public logout() {
    localStorage.clear();
    sessionStorage.clear();
  }

  public getPartnerPublicInfo(): Observable<{ success: string; data: PartnerInterfaceGlobal.IPartnerPublicInfo }> {
    return this.http.get<{ success: string; data: PartnerInterfaceGlobal.IPartnerPublicInfo }>(`${this.apiURl}/partner/public-info`)
      .pipe(catchError(this.handleHttpError.bind(this)));
  }

  public getAllPartnerInfo(): Observable<CheckDomainResponse> {
    return this.http.get<CheckDomainResponse>(`${this.apiURl}/check`)
      .pipe(catchError(this.handleHttpError.bind(this)));
  }

  public get expiredDate(): string {
    let expiredDate = sessionStorage.getItem('expiredDate');

    if (!expiredDate) {
      expiredDate = localStorage.getItem('expiredDate');
    }

    return expiredDate || '';
  }

  public set expiredDate(value: string) {
    sessionStorage.setItem('expiredDate', value);
    localStorage.setItem('expiredDate', value);
  }


  public set token(token: string) {
    localStorage.setItem('token', token);
    sessionStorage.setItem('token', token);
  }

  public get token(): string {
    let token = sessionStorage.getItem('token');

    if (!token) {
      token = localStorage.getItem('token');
    }

    if (!token) {
      return null as any;
    }

    return token;
  }

  public set userRole(value: string) {
    localStorage.setItem('userRole', value);
    sessionStorage.setItem('userRole', value);
  }

  public get userRole(): UserRole {
    let userRole = sessionStorage.getItem('userRole') as UserRole;

    if (!userRole) {
      userRole = localStorage.getItem('userRole') as UserRole;
    }

    return userRole;
  }

  public set userId(value: number) {
    localStorage.setItem('userId', String(value));
    sessionStorage.setItem('userId', String(value));
  }

  public get userId(): string {
    let userId = sessionStorage.getItem('userId');

    if (!userId) {
      userId = localStorage.getItem('userId');
    }

    return String(userId);
  }

  public get mainSspDspTableOpened() {
    return { supply: true, demand: true };
  }

  public set mainSspDspTableOpened(value) {
    localStorage.setItem('main-ssp-dsp-table-opened', JSON.stringify(value));
  }

  public get partner(): Partner {
    const partnerData = localStorage.getItem('partner');
    const partner = partnerData ? JSON.parse(partnerData) : null;
    if (!partner) {
      return new Partner;
    }
    return partner;
  }

  public set partner(partner: Partner) {
    localStorage.setItem('partner', JSON.stringify(partner));
  }

  public isExpired(dateExpired: string): boolean {
    if (!dateExpired) {
      return true; // If no date provided, consider it expired
    }
    // Parse the expiration date string to a Date object
    const parsedExpireDate = new Date(dateExpired);

    // Get the current date and time
    const currentDate = new Date();

    // Compare the current date and time with the expiration date and time
    const isExpired = currentDate > parsedExpireDate;

    return isExpired;
  }

  private handleHttpError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      return _throw({ code: 0, messages: ['Application crashed. Please try to refresh the page.'] });
    }

    // Handle specific HTTP status codes
    if (error.status === 401) {
      if (error.error.hasOwnProperty('has_access_limited')) {
        return _throw({ code: 401, data: error.error });
      } else {
        return _throw({ code: 401, messages: [error.error.error] });
      }
    }

    if (error.status === 403) {
      return _throw({ code: 403, messages: [error.error.message] });
    }

    if (error.status === 404) {
      return _throw({
        code: 404, messages: error.error.error === 'Partner not found'
          ? [error.error.error]
          : ['Can\'t process the request. Requested URL not found.']
      });
    }

    if (error.status === 422) {
      const errors = error.error.errors;
      const messages = errors ? Object.values(errors || {}).map((item) => {
        if (typeof item === 'object') {
          const errorsArray = Object.values(item || {}).flat().join('\r\n');
          return errorsArray;
        } else {
          return item;
        }
      }) : [];
      return _throw({ code: 422, messages, errors });
    }

    if (error.status === 500) {
      const messages: string[] = [];
      if (error.error.errors) {
        for (const i in error.error.errors) {
          messages.push(error.error.errors[i]);
        }
        return _throw({ code: 500, messages });
      } else {
        return _throw({ code: 500, messages: ['Server error. Please try again later.'] });
      }
    }

    if (error.status === 503) {
      return _throw({ code: 503, messages: ['Service Unavailable'] });
    }

    return _throw({ code: 0, messages: ['An error occurred. Please try again later.'] });
  }

  private showNotification(
    iconType: string,
    color: string,
    notificationType: string,
    description: string,
    width: string,
  ): void {
    const data = {
      iconType,
      color,
      notificationType,
      description,
    };

    this.notificationComponent.data = data;
    this.notificationService.showTemplate({ nzData: data, nzStyle: { width: width ?? '385px' } });
  }
}
